import * as XLSX from 'xlsx';

export interface ParsedSheet {
  name: string;
  headers: string[];
  sampleData: string[][];
  headerRowIndex: number;
}

export interface ParsedTemplate {
  fileName: string;
  sheets: ParsedSheet[];
  recommendedSheetIndex: number;
}

/**
 * 엑셀 파일(.xlsx, .xls)을 파싱하여 시트별 헤더와 샘플 데이터를 추출
 */
export async function parseTemplateFile(file: File): Promise<ParsedTemplate> {
  const arrayBuffer = await file.arrayBuffer();
  const workbook = XLSX.read(arrayBuffer, { type: 'array' });

  const sheets: ParsedSheet[] = workbook.SheetNames.map((name) => {
    const worksheet = workbook.Sheets[name];
    const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, {
      header: 1,
      defval: '',
      blankrows: false,
    });

    if (jsonData.length === 0) {
      return { name, headers: [], sampleData: [], headerRowIndex: 0 };
    }

    // 첫 행을 헤더로 사용, Rich text 등은 plain text로 변환
    const rawHeaders = (jsonData[0] || []).map((cell) => normalizeHeaderText(String(cell ?? '')));

    // 스타일만 적용된 빈 셀이 시트 dimension을 부풀려(예: A1:XEO2 = 16369열)
    // 헤더가 수천 개로 잡히는 문제 방어: 헤더 또는 데이터가 실제로 채워진
    // 마지막 열까지만 사용. 스타일뿐인 빈 셀은 헤더·데이터 모두 비어 있어 잘리고,
    // 헤더 없이 값만 있는 trailing 열은 보존된다. 열 인덱스는 유지 → 매핑 무회귀.
    let lastUsedColumn = -1;
    for (const row of jsonData) {
      if (!row) continue;
      for (let i = row.length - 1; i > lastUsedColumn; i--) {
        if (String(row[i] ?? '').trim() !== '') {
          lastUsedColumn = i;
          break;
        }
      }
    }
    const headers = rawHeaders.slice(0, lastUsedColumn + 1);

    const sampleData = jsonData.slice(1, 4).map((row) =>
      row.slice(0, lastUsedColumn + 1).map((cell) => String(cell ?? ''))
    );

    return {
      name,
      headers,
      sampleData,
      headerRowIndex: 1,
    };
  });

  const recommendedSheetIndex = findRecommendedSheet(sheets);

  return {
    fileName: file.name,
    sheets,
    recommendedSheetIndex,
  };
}

/**
 * Rich text, 멀티라인 등의 헤더 텍스트를 정규화
 */
function normalizeHeaderText(text: string): string {
  return text
    .replace(/\r?\n/g, ' ')  // 줄바꿈 제거
    .replace(/\s+/g, ' ')     // 다중 공백 합침
    .trim();
}

/**
 * 추천 시트 선택: 한글 텍스트가 많은 헤더를 가진 시트 우선
 */
function findRecommendedSheet(sheets: ParsedSheet[]): number {
  if (sheets.length <= 1) return 0;

  // 기본값: 첫 번째 비어있지 않은 시트 (없으면 0)
  const firstNonEmpty = sheets.findIndex((s) => s.headers.length > 0);
  let bestIndex = firstNonEmpty >= 0 ? firstNonEmpty : 0;
  let bestScore = -1;

  sheets.forEach((sheet, index) => {
    if (sheet.headers.length === 0) return;

    let score = 0;
    const koreanHeaderCount = sheet.headers.filter((h) => /[가-힣]/.test(h)).length;
    score += koreanHeaderCount * 3;

    // 헤더가 있는 시트 ("제목있음" 등) 가점
    if (/제목.*있/i.test(sheet.name) || /헤더/i.test(sheet.name)) {
      score += 10;
    }
    // "제목없음" 감점
    if (/제목.*없/i.test(sheet.name) || /헤더.*없/i.test(sheet.name)) {
      score -= 5;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIndex = index;
    }
  });

  return bestIndex;
}

/**
 * 파일 유효성 검사
 */
export function validateTemplateFile(file: File): string | null {
  const validExtensions = ['.xlsx', '.xls'];
  const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();

  if (!validExtensions.includes(ext)) {
    return '지원하지 않는 파일 형식입니다. .xlsx 또는 .xls 파일을 업로드해주세요.';
  }

  const maxSize = 5 * 1024 * 1024; // 5MB
  if (file.size > maxSize) {
    return '파일 크기가 너무 큽니다. 5MB 이하의 파일을 업로드해주세요.';
  }

  return null;
}

/**
 * 매핑 가능한 내부 필드 목록
 */
export const MAPPING_FIELDS = [
  { value: 'null', label: '매핑 안 함' },
  { value: 'name', label: '이름' },
  { value: 'phone_number', label: '전화번호' },
  { value: 'address', label: '주소 (전체)' },
  { value: 'address_base', label: '기본주소' },
  { value: 'address_detail', label: '상세주소' },
  { value: 'delivery_notes', label: '배송메모' },
  { value: 'product_name', label: '상품명' },
  { value: 'nickname', label: '닉네임' },
] as const;

export type MappingFieldValue = typeof MAPPING_FIELDS[number]['value'];

export interface ColumnMapping {
  columnName: string;
  mappedField: MappingFieldValue;
}

export interface DeliveryTemplate {
  id?: number;
  name: string;
  columnMappings: ColumnMapping[];
  headerRowIndex: number;
  createdAt?: string;
  modifiedAt?: string;
}

/**
 * 파일명에서 기본 양식 이름 추출
 */
export function getDefaultTemplateName(fileName: string): string {
  return fileName.replace(/\.(xlsx|xls)$/i, '').trim();
}
